/**
 * Search engine analytics – Hotel Name Index, aliases, autocorrect, Did You Mean.
 * Events are sent to gtag (if present) and optionally to console. No API/booking flow changes.
 *
 * Events:
 * - search_match: once per hotel-name search; match type (EXACT|PHRASE|PREFIX|TOKEN|FUZZY).
 * - did_you_mean_shown: when the "Did you mean" banner is displayed.
 * - did_you_mean_clicked: when the user clicks Apply on the banner.
 *
 * Deduplication: one event per logical action (one per search run, one per show, one per click).
 */

const EVENT_SEARCH_MATCH = 'search_match';
const EVENT_DID_YOU_MEAN_SHOWN = 'did_you_mean_shown';
const EVENT_DID_YOU_MEAN_CLICKED = 'did_you_mean_clicked';

const GA_CATEGORY = 'search_engine';

/**
 * Send event to gtag and optionally log. Does not throw.
 * @param {string} eventName
 * @param {Record<string, unknown>} payload
 */
function send(eventName, payload) {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: GA_CATEGORY,
        ...payload,
      });
    }
    if (process.env.NODE_ENV === 'development') {
      console.info('[searchAnalytics]', eventName, payload);
    }
  } catch (e) {
    // Ignore analytics failures
  }
}

/**
 * Track hotel-name search result: match type for the search (EXACT/PHRASE/PREFIX/TOKEN/FUZZY).
 * Call once per successful search when intent is HOTEL_SEARCH.
 *
 * @param {{
 *   runId: string | number;
 *   intent: string;
 *   normalizedQuery: string;
 *   matchType: string | null;
 *   hasFuzzyApplied: boolean;
 *   totalResults: number;
 *   firstHotelDedupKey: string | null;
 *   intendedPosition: number | null;
 *   intendedHotelName?: string | null;
 * }} payload
 */
export function trackSearchMatch(payload) {
  send(EVENT_SEARCH_MATCH, {
    run_id: payload.runId,
    intent: payload.intent,
    normalized_query: payload.normalizedQuery,
    match_type: payload.matchType ?? 'none',
    has_fuzzy_applied: payload.hasFuzzyApplied,
    total_results: payload.totalResults,
    first_hotel_dedup_key: payload.firstHotelDedupKey,
    intended_position: payload.intendedPosition,
    intended_hotel_name: payload.intendedHotelName ?? undefined,
  });
}

/**
 * Track "Did you mean" banner shown. Call once when autocorrect suggestion is set for display.
 *
 * @param {{
 *   runId?: string | number;
 *   originalQuery: string;
 *   suggestedQuery: string;
 *   confidence: number;
 *   candidateSource?: string;
 * }} payload
 */
export function trackDidYouMeanShown(payload) {
  send(EVENT_DID_YOU_MEAN_SHOWN, {
    run_id: payload.runId,
    original_query: payload.originalQuery,
    suggested_query: payload.suggestedQuery,
    confidence: payload.confidence,
    candidate_source: payload.candidateSource,
  });
}

/**
 * Track user clicked "Did you mean" Apply. Call once per click.
 *
 * @param {{
 *   originalQuery: string;
 *   suggestedQuery: string;
 * }} payload
 */
export function trackDidYouMeanClicked(payload) {
  send(EVENT_DID_YOU_MEAN_CLICKED, {
    original_query: payload.originalQuery,
    suggested_query: payload.suggestedQuery,
  });
}

export { EVENT_SEARCH_MATCH, EVENT_DID_YOU_MEAN_SHOWN, EVENT_DID_YOU_MEAN_CLICKED };
